import { useState } from "react";
import { OrderLineTransferTable } from "@/components/search-sales-orders/OrderLineTransferTable";
import { RowDetailDrawer } from "@/components/search-sales-orders/RowDetailDrawer";
import { SearchFiltersPanel } from "@/components/search-sales-orders/SearchFiltersPanel";
import { SetValuesPanel } from "@/components/search-sales-orders/SetValuesPanel";
import {
  EMPTY_SEARCH_FILTERS,
  EMPTY_SET_VALUES,
  matchesSearchFilters,
  type SalesOrderLine,
} from "@/mocks/search-sales-orders";

interface SalesOrdersWorkspaceProps {
  lines: SalesOrderLine[];
}

function toggleInSet(set: Set<string>, id: string, checked: boolean): Set<string> {
  const next = new Set(set);
  if (checked) next.add(id);
  else next.delete(id);
  return next;
}

export function SalesOrdersWorkspace({ lines }: SalesOrdersWorkspaceProps) {
  const [filters, setFilters] = useState(EMPTY_SEARCH_FILTERS);
  const [setValues, setSetValues] = useState(EMPTY_SET_VALUES);
  const [hasSearched, setHasSearched] = useState(false);
  const [leftRows, setLeftRows] = useState<SalesOrderLine[]>([]);
  const [rightRows, setRightRows] = useState<SalesOrderLine[]>([]);
  const [leftChecked, setLeftChecked] = useState<Set<string>>(new Set());
  const [rightChecked, setRightChecked] = useState<Set<string>>(new Set());
  const [selectedLine, setSelectedLine] = useState<SalesOrderLine | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  function handleSearch() {
    const selectedIds = new Set(rightRows.map((row) => row.lineId));
    const matches = lines.filter(
      (candidate) => matchesSearchFilters(candidate, filters) && !selectedIds.has(candidate.lineId),
    );
    setLeftRows(matches);
    setLeftChecked(new Set());
    setHasSearched(true);
  }

  function handleClear() {
    setFilters(EMPTY_SEARCH_FILTERS);
    setSetValues(EMPTY_SET_VALUES);
    setLeftRows([]);
    setRightRows([]);
    setLeftChecked(new Set());
    setRightChecked(new Set());
    setHasSearched(false);
    setDrawerOpen(false);
  }

  function handleMoveRight() {
    const moving = leftRows.filter((row) => leftChecked.has(row.lineId));
    setLeftRows(leftRows.filter((row) => !leftChecked.has(row.lineId)));
    setRightRows([...rightRows, ...moving]);
    setLeftChecked(new Set());
  }

  function handleMoveLeft() {
    const moving = rightRows.filter((row) => rightChecked.has(row.lineId));
    setRightRows(rightRows.filter((row) => !rightChecked.has(row.lineId)));
    setLeftRows([...leftRows, ...moving]);
    setRightChecked(new Set());
  }

  function handleRowClick(row: SalesOrderLine) {
    setSelectedLine(row);
    setDrawerOpen(true);
  }

  const leftEmptyMessage = hasSearched
    ? "No results yet — run a search above."
    : "Run a search to load matching order lines.";
  const leftCountText = leftRows.length
    ? `${leftRows.length} line${leftRows.length === 1 ? "" : "s"} matched.`
    : leftEmptyMessage;
  const rightCountText = rightRows.length
    ? `${rightRows.length} line${rightRows.length === 1 ? "" : "s"} selected.`
    : "Nothing selected yet.";

  return (
    <div className="space-y-6">
      <SearchFiltersPanel values={filters} onChange={setFilters} onSearch={handleSearch} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <OrderLineTransferTable
          title="Search results"
          countText={leftCountText}
          rows={leftRows}
          emptyMessage={leftEmptyMessage}
          checkedIds={leftChecked}
          onToggleRow={(id, checked) => setLeftChecked(toggleInSet(leftChecked, id, checked))}
          onSelectAll={() => setLeftChecked(new Set(leftRows.map((row) => row.lineId)))}
          onSelectNone={() => setLeftChecked(new Set())}
          moveLabel="Add to selected →"
          moveDisabled={leftChecked.size === 0}
          onMove={handleMoveRight}
          onRowClick={handleRowClick}
        />

        <OrderLineTransferTable
          title="Selected for update"
          countText={rightCountText}
          rows={rightRows}
          emptyMessage="Move records here from Search results to include them in the update."
          checkedIds={rightChecked}
          onToggleRow={(id, checked) => setRightChecked(toggleInSet(rightChecked, id, checked))}
          onSelectAll={() => setRightChecked(new Set(rightRows.map((row) => row.lineId)))}
          onSelectNone={() => setRightChecked(new Set())}
          moveLabel="← Remove"
          moveDisabled={rightChecked.size === 0}
          onMove={handleMoveLeft}
          onRowClick={handleRowClick}
        />
      </div>

      <SetValuesPanel
        values={setValues}
        onChange={setSetValues}
        selectedCount={rightRows.length}
        onClear={handleClear}
      />

      <RowDetailDrawer line={selectedLine} open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  );
}
